/**
 * Event Types
 *
 * Type definitions for calendar events.
 * Includes event structure, recurrence, and reminders.
 *
 * @module features/family-calendar/types/event.types
 */

// ============================================
// Core Event Types
// ============================================

/**
 * Calendar event entity.
 * Represents a scheduled activity or reminder on a calendar.
 */
export interface CalendarEvent {
  /** Unique identifier */
  readonly id: string;

  /** Reference to parent calendar */
  readonly calendarId: string;

  /** Event title (required) */
  readonly title: string;

  /** Detailed description (supports markdown-lite) */
  readonly description?: string;

  /** ASCII emoji for quick visual identification */
  readonly emoji?: string;

  /** Start date/time in ISO 8601 format */
  readonly startDate: string;

  /** End date/time in ISO 8601 format (optional for single-point events) */
  readonly endDate?: string;

  /** Whether event spans entire day(s) */
  readonly isAllDay: boolean;

  /** Physical or virtual location */
  readonly location?: string;

  /** External URL (video call, website, etc.) */
  readonly url?: string;

  /** Reference to EventLabel.id for categorization */
  readonly labelId?: string;

  /**
   * `profiles.id` of the creator — i.e. `auth.uid()`, the value
   * `events_insert_member`'s WITH CHECK forces onto `events.created_by`.
   *
   * NOT a `family_members.id`, and the distinction is not academic: there are
   * TWO types named `FamilyMember` in this repo. The one `calendarStore.members`
   * holds (`shared/types/member.types.ts`) has `id` = a PROFILE uuid, by
   * convention — `mapActiveFamilyMembers` sets `id: m.profileId`. The other
   * (`features/family/types/family.types.ts`) has `id` = `family_members.id`,
   * an independent `gen_random_uuid()` that is never equal to a profile id.
   * This comment used to say "FamilyMember.id", which sends a reader to the
   * wrong one and turns every name lookup into a Map miss.
   */
  readonly createdBy: string;

  /** Array of FamilyMember.ids who are participating */
  readonly participants: readonly string[];

  /** Recurrence configuration (if repeating event) */
  readonly recurrence?: RecurrenceRule;

  /**
   * The master event this row was generated from — set only when
   * {@link CalendarEvent.isOccurrence} is true, absent on authored events.
   * Mirrors `events.parent_event_id` (migration 006).
   */
  readonly parentEventId?: string;

  /**
   * True when `expand-recurring-events` materialized this row from a master
   * rather than a family member authoring it. Mirrors `events.is_occurrence`.
   *
   * WHY IT HAS TO BE ON THE DOMAIN EVENT. A generated occurrence carries
   * `recurrence: null` — only the master keeps the rule — so `recurrence` is
   * useless as a "belongs to a series" test: the occurrence looks exactly like
   * a one-off event. This flag is the ONLY thing that distinguishes them, and
   * dropping it in the mapper is what made a generated row indistinguishable
   * from an authored one all the way up the stack. It also has teeth: the
   * expander's upsert keys on `(parent_event_id, start_date)` with
   * `ignoreDuplicates`, so DELETING an occurrence does not remove it from the
   * series — the next expander run re-materializes it at the same instant.
   */
  readonly isOccurrence: boolean;

  /** Reminder configurations */
  readonly reminders: readonly Reminder[];

  /** Whether event is pinned to top */
  readonly isPinned: boolean;

  /** Notes visible only to the creator */
  readonly privateNotes?: string;

  /** Comments from family members */
  readonly comments: readonly EventComment[];

  /** File attachments */
  readonly attachments: readonly EventAttachment[];

  /** Record creation timestamp */
  readonly createdAt: string;

  /** Record last update timestamp */
  readonly updatedAt: string;
}

/**
 * Minimal event reference for calendar grid display
 */
export type CalendarEventRef = Pick<
  CalendarEvent,
  | 'id'
  | 'title'
  | 'emoji'
  | 'startDate'
  | 'endDate'
  | 'isAllDay'
  | 'labelId'
  | 'participants'
>;

// ============================================
// Change History Types
// ============================================

/**
 * User-meaningful event fields tracked for field-level change history.
 * Drives the diff loop in the store when an event is edited.
 */
export const TRACKED_EVENT_FIELDS = [
  'title',
  'emoji',
  'description',
  'startDate',
  'endDate',
  'isAllDay',
  'location',
  'url',
  'labelId',
  'recurrence',
  'reminders',
  'isPinned',
] as const;

/**
 * Union of tracked event field keys.
 */
export type EventChangedField = (typeof TRACKED_EVENT_FIELDS)[number];

/**
 * A single field-level change record, produced (forward-only) when an event
 * is edited. Values are pre-formatted display strings; `null` means unset.
 */
export interface EventChangeRecord {
  /** Which field changed */
  readonly field: EventChangedField;

  /** Previous value as a display string, or `null` when previously unset */
  readonly oldValue: string | null;

  /** New value as a display string, or `null` when cleared */
  readonly newValue: string | null;

  /**
   * `profiles.id` of the editor — i.e. `auth.uid()`, the value
   * `event_changes_insert_member` forces onto `event_changes.changed_by`.
   *
   * THE READ MAPPER PASSES `changed_by` THROUGH UNCHANGED. No lookup, no join,
   * no translation — `ActivityFeed`'s `memberMap` is keyed on
   * `calendarStore.members[].id`, which `mapActiveFamilyMembers` sets to
   * `m.profileId`, so the two id spaces already coincide. Any "fix" that
   * translates this to a `family_members.id` (the OTHER type named
   * `FamilyMember`, see {@link CalendarEvent.createdBy}) compiles cleanly and
   * silently renders „Неизвестен" on every row.
   */
  readonly changedBy: string;

  /** ISO timestamp shared by all records in the same edit batch */
  readonly changedAt: string;
}

/**
 * Partial update payload for `updateEvent`.
 *
 * Unlike `Partial<CalendarEvent>`, optional fields may be set explicitly to
 * `undefined` to CLEAR a previously-set value. An omitted key leaves the
 * existing value unchanged, whereas an explicit `undefined` clears it (and
 * produces a "removed" field-level change record).
 */
export type EventUpdate = {
  [K in keyof CalendarEvent]?: CalendarEvent[K] | undefined;
};

// ============================================
// Recurrence Types
// ============================================

/**
 * Recurrence frequency options
 */
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

/**
 * Days of the week for weekly recurrence
 */
export type DayOfWeek = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

/**
 * Recurrence rule for repeating events.
 * Based on RFC 5545 (iCalendar) RRULE specification.
 */
export interface RecurrenceRule {
  /** How often the event repeats */
  readonly frequency: RecurrenceFrequency;

  /** Repeat every N periods (e.g., every 2 weeks) */
  readonly interval: number;

  /** Days of week for weekly recurrence */
  readonly byDay?: readonly DayOfWeek[];

  /** Day of month for monthly recurrence (1-31) */
  readonly byMonthDay?: number;

  /** Month for yearly recurrence (1-12) */
  readonly byMonth?: number;

  /** End date for recurrence (ISO 8601) */
  readonly until?: string;

  /** Maximum number of occurrences */
  readonly count?: number;

  /**
   * Instants to exclude from the recurrence (ISO 8601).
   *
   * COMPARED BY UTC DAY, on both engines. `expandOccurrences` builds
   * `new Set(exDates.map((iso) => toUtcDayStart(new Date(iso)).getTime()))` and
   * skips any instant whose UTC day is in that set, so ONE entry cancels an
   * entire UTC day of this series. That is lossless only because every branch
   * of the engine emits at most one instant per UTC day; it becomes silent
   * over-deletion the day a BYHOUR or twice-daily rule is added.
   *
   * The corollary that bites sooner: an entry must be the occurrence's exact
   * start INSTANT, never a local date key. A 23:30-local event in UTC+3 sits on
   * the NEXT UTC day, so `toLocalDateKey` is the wrong function here.
   */
  readonly exDates?: readonly string[];
}

// ============================================
// Reminder Types
// ============================================

/**
 * Reminder notification timing
 */
export type ReminderTiming =
  | 'at_time'
  | '5_min'
  | '15_min'
  | '30_min'
  | '1_hour'
  | '1_day'
  | '1_week';

/**
 * Reminder delivery method
 */
export type ReminderMethod = 'notification' | 'email';

/**
 * Reminder configuration for event notifications
 */
export interface Reminder {
  /** Unique identifier */
  readonly id: string;

  /** When to send the reminder relative to event start */
  readonly timing: ReminderTiming;

  /** How to deliver the reminder */
  readonly method: ReminderMethod;

  /** Array of FamilyMember.ids to notify */
  readonly recipients: readonly string[];
}

// ============================================
// Comment Types
// ============================================

/**
 * Comment on a calendar event.
 * Allows family members to discuss events.
 */
export interface EventComment {
  /** Unique identifier */
  readonly id: string;

  /** Reference to parent event */
  readonly eventId: string;

  /** Comment text content */
  readonly text: string;

  /** FamilyMember.id of the author */
  readonly authorId: string;

  /** When the comment was posted */
  readonly createdAt: string;

  /** When the comment was last edited (optional) */
  readonly updatedAt?: string;
}

// ============================================
// Attachment Types
// ============================================

/**
 * Supported file types for attachments
 */
export type AttachmentFileType =
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'other';

/**
 * File extension to file type mapping
 */
export const FILE_TYPE_EXTENSIONS: Readonly<
  Record<string, AttachmentFileType>
> = {
  // Images
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  // PDF
  pdf: 'pdf',
  // Documents
  doc: 'document',
  docx: 'document',
  odt: 'document',
  rtf: 'document',
  txt: 'document',
  // Spreadsheets
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  ods: 'spreadsheet',
  csv: 'spreadsheet',
} as const;

/**
 * Bulgarian labels for file types
 */
export const FILE_TYPE_LABELS: Readonly<Record<AttachmentFileType, string>> = {
  image: 'Изображение',
  pdf: 'PDF документ',
  document: 'Документ',
  spreadsheet: 'Таблица',
  other: 'Файл',
} as const;

/**
 * File attachment for calendar events.
 * Allows attaching documents, images, and other files.
 */
export interface EventAttachment {
  /** Unique identifier */
  readonly id: string;

  /** Original filename */
  readonly filename: string;

  /** File URL (for download/preview) */
  readonly url: string;

  /**
   * Storage path within the private `attachments` bucket
   * (`<family_id>/<entity_id>/<filename>`). Set by FileUploadArea once the file
   * reaches Supabase Storage; used to (re)generate signed download URLs and to
   * persist the `event_attachments` row. Attachments without it are never
   * persisted.
   */
  readonly storagePath?: string;

  /** File size in bytes */
  readonly size: number;

  /** MIME type */
  readonly mimeType: string;

  /** Categorized file type */
  readonly fileType: AttachmentFileType;

  /** Thumbnail URL for images (optional) */
  readonly thumbnailUrl?: string;

  /**
   * True when the object could not be signed on the last read (deleted file,
   * orphan row, policy refusal). `url` is `''` in that case; the attachment is
   * still listed with its filename, size and type so the row is explicable
   * rather than missing.
   */
  readonly isUnavailable?: boolean;

  /** FamilyMember.id who uploaded the file */
  readonly uploadedBy: string;

  /** When the file was uploaded */
  readonly uploadedAt: string;
}

/**
 * Helper function to get file type from extension
 */
export function getFileTypeFromExtension(filename: string): AttachmentFileType {
  const extension = filename.split('.').pop()?.toLowerCase() ?? '';
  return FILE_TYPE_EXTENSIONS[extension] ?? 'other';
}

/**
 * Helper function to format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

// ============================================
// Date Range Types
// ============================================

/**
 * Date range for filtering events
 */
export interface DateRange {
  /** Start of range (ISO 8601) */
  readonly start: string;

  /** End of range (ISO 8601) */
  readonly end: string;
}

// ============================================
// Constants
// ============================================

/**
 * All recurrence frequency options
 */
export const ALL_RECURRENCE_FREQUENCIES: readonly RecurrenceFrequency[] = [
  'daily',
  'weekly',
  'monthly',
  'yearly',
] as const;

/**
 * Bulgarian labels for recurrence frequencies
 */
export const RECURRENCE_FREQUENCY_LABELS: Readonly<
  Record<RecurrenceFrequency, string>
> = {
  daily: 'Всеки ден',
  weekly: 'Всяка седмица',
  monthly: 'Всеки месец',
  yearly: 'Всяка година',
} as const;

/**
 * All days of the week
 */
export const ALL_DAYS_OF_WEEK: readonly DayOfWeek[] = [
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
] as const;

/**
 * Bulgarian short labels for days of week
 */
export const DAY_OF_WEEK_LABELS: Readonly<Record<DayOfWeek, string>> = {
  MO: 'Пон',
  TU: 'Вто',
  WE: 'Сря',
  TH: 'Чет',
  FR: 'Пет',
  SA: 'Съб',
  SU: 'Нед',
} as const;

/**
 * Bulgarian full labels for days of week
 */
export const DAY_OF_WEEK_FULL_LABELS: Readonly<Record<DayOfWeek, string>> = {
  MO: 'Понеделник',
  TU: 'Вторник',
  WE: 'Сряда',
  TH: 'Четвъртък',
  FR: 'Петък',
  SA: 'Събота',
  SU: 'Неделя',
} as const;

/**
 * All reminder timing options
 */
export const ALL_REMINDER_TIMINGS: readonly ReminderTiming[] = [
  'at_time',
  '5_min',
  '15_min',
  '30_min',
  '1_hour',
  '1_day',
  '1_week',
] as const;

/**
 * Bulgarian labels for reminder timings
 */
export const REMINDER_TIMING_LABELS: Readonly<Record<ReminderTiming, string>> =
  {
    at_time: 'В момента на събитието',
    '5_min': '5 минути преди',
    '15_min': '15 минути преди',
    '30_min': '30 минути преди',
    '1_hour': '1 час преди',
    '1_day': '1 ден преди',
    '1_week': '1 седмица преди',
  } as const;

/**
 * All reminder methods
 */
export const ALL_REMINDER_METHODS: readonly ReminderMethod[] = [
  'notification',
  'email',
] as const;

/**
 * Bulgarian labels for reminder methods
 */
export const REMINDER_METHOD_LABELS: Readonly<Record<ReminderMethod, string>> =
  {
    notification: 'Известие',
    email: 'Имейл',
  } as const;

// ============================================
// Type Guards
// ============================================

/**
 * Type guard for RecurrenceFrequency
 */
export function isRecurrenceFrequency(
  value: unknown
): value is RecurrenceFrequency {
  return (
    typeof value === 'string' &&
    ALL_RECURRENCE_FREQUENCIES.includes(value as RecurrenceFrequency)
  );
}

/**
 * Type guard for DayOfWeek
 */
export function isDayOfWeek(value: unknown): value is DayOfWeek {
  return (
    typeof value === 'string' && ALL_DAYS_OF_WEEK.includes(value as DayOfWeek)
  );
}

/**
 * Type guard for ReminderTiming
 */
export function isReminderTiming(value: unknown): value is ReminderTiming {
  return (
    typeof value === 'string' &&
    ALL_REMINDER_TIMINGS.includes(value as ReminderTiming)
  );
}

// ============================================
// Series Scope Types (Wave 8, Major 2)
// ============================================

// A DEAD `CreateEventDTO` / `UpdateEventDTO` pair used to live here. It had
// ZERO importers — every caller takes the pair from
// `services/calendarService.types.ts` — and it was actively dangerous: the dead
// `UpdateEventDTO.recurrence` already allowed `| null`, so anyone fixing
// "a recurrence rule cannot be cleared" by grepping the type name would edit
// THIS copy, compile cleanly, and change nothing at all. Deleted in Wave 8; the
// live pair is the only pair.

/**
 * The occurrence a scoped series write is aimed at.
 *
 * Every leg carries `(masterId, occurrenceStart)` rather than a row id, because
 * the target may be a PROJECTION — an instant the expander has not materialised
 * yet, which has no row to name. See `services/recurrenceScope.ts`.
 */
export interface SeriesTarget {
  /** The series master: `recurrence is not null` and `parent_event_id is null`. */
  readonly masterId: string;

  /**
   * ISO instant of the targeted occurrence. Equals the master's `startDate`
   * when the master's own instant is the target (the master IS occurrence #1).
   */
  readonly occurrenceStart: string;

  /** The real row for that instant, or `null` when the target is a projection. */
  readonly occurrenceId: string | null;

  /** True when {@link SeriesTarget.occurrenceStart} is the master's own instant. */
  readonly isMasterOccurrence: boolean;
}

/**
 * Instants computed CLIENT-SIDE by `recurrenceScope.ts` and passed to the RPCs
 * as `p_instants`.
 *
 * THE DATABASE GETS NO RRULE IMPLEMENTATION. Every instant either RPC needs is
 * derived here from the same `expandOccurrences` the expander cron runs, so
 * client and server can never disagree about what the rule produces — a third
 * copy of the engine is precisely the trap this repo has already been bitten by
 * having two of.
 */
export interface SeriesInstants {
  /**
   * Scope `this`: the instant to add to the master's EXDATE list, or `null` for
   * „none owed".
   *
   * THE KEY'S THREE STATES ARE THREE DIFFERENT INSTRUCTIONS, and the SQL reads
   * them with `p_instants ? 'exDate'` — key-absent and key-present-with-null are
   * NOT the same thing there:
   *   * ABSENT — „decide yourself". `delete_event_series` / `update_event_series`
   *     then write the exDate for a projection or a generated child (both are
   *     provably rule instants) and SKIP it for an AUTHORED exception, which
   *     they cannot classify without a rule engine they deliberately do not
   *     have.
   *   * `null`  — „no exDate is owed". The instant is not one the rule
   *     generates, so naming it would cancel the whole UTC DAY it sits on.
   *   * ISO     — write exactly this.
   *
   * Computed by `recurrenceScope.seriesExDate` and appended locally by the store
   * as the SAME string, so `applySeriesResult` reconciling the returned master
   * can never revert a correct optimistic rule.
   */
  readonly exDate?: string | null;
  /** Anchor roll: the first surviving occurrence the master advances onto. */
  readonly nextStart?: string;
  /** Anchor roll: `nextStart` plus the master's duration. */
  readonly nextEnd?: string;
  /** EDIT `this`: the destination instant, when the occurrence is MOVED. */
  readonly newStart?: string;
  /** EDIT `this`: the destination end instant. */
  readonly newEnd?: string;
  /** EDIT `this_and_following`: client-minted id for the tail master. */
  readonly tailId?: string;
  /** EDIT `this_and_following`: the tail's COUNT, when the rule had one. */
  readonly tailCount?: number;
}

/**
 * What both series RPCs answer with, mapped out of their `jsonb` return.
 *
 * `master` / `exception` / `tail` are whole `events` rows (or null) so the store
 * can reconcile without a second round trip; `removedIds` is every row the leg
 * deleted, including rows removed by the FK cascade.
 */
export interface SeriesWriteResult {
  readonly master: CalendarEvent | null;
  readonly exception: CalendarEvent | null;
  readonly tail: CalendarEvent | null;
  readonly removedIds: readonly string[];
}

/**
 * Options for updating recurring events
 */
export type RecurringEventUpdateScope = 'this' | 'this_and_following' | 'all';

/**
 * Bulgarian labels for recurring event update scopes
 */
export const RECURRING_UPDATE_SCOPE_LABELS: Readonly<
  Record<RecurringEventUpdateScope, string>
> = {
  this: 'Само това събитие',
  this_and_following: 'Това и следващите',
  all: 'Всички събития',
} as const;

// ============================================
// Calendar Sharing Types
// ============================================

/**
 * Expiration duration options for share links
 */
export type ShareLinkExpiration =
  | '1_hour'
  | '24_hours'
  | '7_days'
  | '30_days'
  | 'never';

/**
 * Bulgarian labels for share link expiration options
 */
export const SHARE_LINK_EXPIRATION_LABELS: Readonly<
  Record<ShareLinkExpiration, string>
> = {
  '1_hour': '1 час',
  '24_hours': '24 часа',
  '7_days': '7 дни',
  '30_days': '30 дни',
  never: 'Без изтичане',
} as const;

/**
 * Permission level for share links
 */
export type ShareLinkPermission = 'view' | 'edit';

/**
 * Bulgarian labels for share link permissions
 */
export const SHARE_LINK_PERMISSION_LABELS: Readonly<
  Record<ShareLinkPermission, string>
> = {
  view: 'Само преглед',
  edit: 'Преглед и редакция',
} as const;

/**
 * Shareable link for calendar access, backed by `calendar_share_links`. The
 * token is the capability and resolves at the public `/shared/:token` route.
 * `permission` is always `'view'` and `usageCount` always `0` — neither has a
 * column to read from.
 */
export interface ShareLink {
  /** Unique identifier */
  readonly id: string;

  /** Reference to the calendar being shared */
  readonly calendarId: string;

  /** The shareable URL */
  readonly url: string;

  /** Short code for easy sharing */
  readonly code: string;

  /** Permission level granted by this link */
  readonly permission: ShareLinkPermission;

  /** When the link expires (ISO 8601), null if never */
  readonly expiresAt: string | null;

  /** When the link was created */
  readonly createdAt: string;

  /** FamilyMember.id who created the link */
  readonly createdById: string;

  /** Display name of the creator captured at generation time (resolution fallback) */
  readonly createdByName?: string;

  /** Whether the link is currently active */
  readonly isActive: boolean;

  /** Number of times the link was used */
  readonly usageCount: number;
}
